import * as React from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { useIsMobile } from "@/hooks/useIsMobile";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ApartmentIcon from "@mui/icons-material/Apartment";
import FactoryIcon from "@mui/icons-material/Factory";
import LocalHotelIcon from "@mui/icons-material/LocalHotel";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import {
  IconButton,
  Stack,
  ToggleButton,
  TextField,
  Radio as MuiRadio,
  Typography,
  RadioGroup as MuiRadioGroup,
  FormControlLabel as MuiFormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Select,
} from "@mui/material";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { Button, ButtonVariantOption } from "@/components/Button";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MultiSelect, Select as CSelect } from "@/components/Select";
import { usePrefectures } from "@/hooks/usePrefectures";
import { useCityWithExternalState } from "@/hooks/useCity";
import { Checkbox, CheckboxGroup } from "@/components/Checkbox";
import {
  makeRangeSchemaWithRestrictMinMax,
  makeAreaRangeSchema,
  makeBigIntRangeSchema,
  makeIntegerRangeSchema,
  makeNullableDateRangeSchema,
} from "@/utils/utilsReactHookForms";
import { getYear } from "date-fns";
import { Label } from "@/components/Label";
import { DateRangePicker } from "@/components/DatePicker";
import { getSearchMaxDate } from "@/features/realEstateReceptionBookFeed/utils/dateTime";
import { BUILDING_VARIANT_OPTIONS } from "@/features/titleSearch/components/BuildingSearchAccordion";
import {
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWhenPaste,
} from "@/utils/utils";
import Tooltip from "@mui/material/Tooltip";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useApiClient } from "../../../hooks/useApiClient";
import { toast } from "react-toastify";
import { HttpStatusCode } from "axios";
import { getMapRealEstateSearchAPI } from "../api/getMapRealEstateSearch";
import { needsLandAddress } from "../utils/realEstateSearch/landAddress";
import { type RealEstateSearchResult } from "../types";
import { useAreaUsePurposeOptions } from "@/hooks/useAreaUsePurposeOptions";
import { useHotelCategoryOptions } from "@/hooks/useHotelCategoryOptions";
import { getBookTypeFromRealEstateTypeLabel } from "./AcquireMultipleBooksModal";
import * as Sentry from "@sentry/react";
import { LatLng } from "leaflet";

// 不動産検索で一覧を受け取れる最大件数。BE（MAX_SEARCH_RESULT_COUNT）と同じ値にする。
// これを超えると BE は件数だけを返す
const MAX_SEARCH_RESULT_COUNT = 2_000;

// 不動産種別のマップ
export const REAL_ESTATE_TYPE_MAP = {
  land: { label: "土地", value: "land" },
  condominium: { label: "区分建物", value: "kubun_building" },
  building: { label: "建物", value: "building" },
};

// 容積率で入力可能な最小値、最小値を定義する。
const YOUSEKI_RATIO_MIN = 50;
const YOUSEKI_RATIO_MAX = 1300;
// 建ぺい率で入力可能な最小値、最小値を定義する。
const KENPEI_RATIO_MIN = 30;
const KENPEI_RATIO_MAX = 80;
// 築年数で入力可能な最小値を定義する。
const BUILDING_AGE_MIN = 0;
// 築年数で入力可能な最大値を定義する。
// BEのコードで、現在日時のyearからMAX+1を引いた年をstart_dateとしている。
// datetime()のyearに0以下は指定できないので、最大値は現在日時のyear-2とする。
const BUILDING_AGE_MAX = getYear(new Date()) - 2;
// 地上階で入力可能な最小値、最大値を定義する。
const GROUND_FLOORS_MIN = 0;
const GROUND_FLOORS_MAX = 100;
// 地下階で入力可能な最小値、最大値を定義する。
const UNDERGROUND_FLOORS_MIN = 0;
const UNDERGROUND_FLOORS_MAX = 100;
// 駐車場数で入力可能な最小値、最大値を定義する。
const PARKING_SPACES_MIN = 0;
const PARKING_SPACES_MAX = 1000;
// 家屋番号数で入力可能な最小値、最大値を定義する。
const KAOKU_NUMBER_MIN = 0;
const KAOKU_NUMBER_MAX = 10000;
// 文字列フィールドの最大文字数を定義する。
const ADDRESS_MAX_LENGTH = 255;
const PROPERTY_NAME_MAX_LENGTH = 255;
const OWNER_NAME_MAX_LENGTH = 255;
const OWNER_ADDRESS_MAX_LENGTH = 255;
const HOTEL_NAME_MAX_LENGTH = 255;
const DORMITORY_NAME_MAX_LENGTH = 255;
const PARKING_LOT_NAME_MAX_LENGTH = 255;
const INDUSTRIAL_FACILITY_NAME_MAX_LENGTH = 255;
// ホテル 部屋数で入力可能な最小値、最大値を定義する。
const HOTEL_ROOM_COUNT_MIN = 0;
const HOTEL_ROOM_COUNT_MAX = 10000;
// 築年で入力可能な最小値を定義する。
const BUILD_DATE_MIN = new Date(1900, 0, 1);
const DORMITORY_KIND_VALUES = ["分譲", "一棟", "不明"] as const;
const DORMITORY_KIND_OPTIONS = DORMITORY_KIND_VALUES.map((value) => ({
  label: value,
  value,
}));

// Zodスキーマ定義
const FormDataSchema = z.object({
  prefecture: z.array(z.number()).nonempty("都道府県を選択してください"),
  city: z.array(z.number()).nonempty("市区町村を選択してください"),
  address: z
    .string()
    .max(ADDRESS_MAX_LENGTH, `${ADDRESS_MAX_LENGTH}文字以内で入力してください`),
  areaUsePurpose: z.array(z.number()),
  yousekiRatio: makeRangeSchemaWithRestrictMinMax(
    YOUSEKI_RATIO_MIN,
    YOUSEKI_RATIO_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  kenpeiRatio: makeRangeSchemaWithRestrictMinMax(
    KENPEI_RATIO_MIN,
    KENPEI_RATIO_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  realEstateType: z
    .string()
    .trim()
    .refine((value) => value.length > 0, {
      message: "不動産種別を選択してください",
    }),
  ownerName: z
    .string()
    .max(
      OWNER_NAME_MAX_LENGTH,
      `${OWNER_NAME_MAX_LENGTH}文字以内で入力してください`
    ),
  ownerAddress: z
    .string()
    .max(
      OWNER_ADDRESS_MAX_LENGTH,
      `${OWNER_ADDRESS_MAX_LENGTH}文字以内で入力してください`
    ),
  landType: z.object({
    farm: z.boolean(),
    solar: z.boolean(),
    parkingLot: z.boolean(),
  }),
  parkingLotName: z
    .string()
    .max(
      PARKING_LOT_NAME_MAX_LENGTH,
      `${PARKING_LOT_NAME_MAX_LENGTH}文字以内で入力してください`
    ),
  totalParkingLotArea: makeAreaRangeSchema(
    "「以上」は「以下」より小さくしてください"
  ),
  estimateAcreage: makeAreaRangeSchema(
    "「以上」は「以下」より小さくしてください"
  ),
  landPrice: makeBigIntRangeSchema("「以上」は「以下」より小さくしてください"),
  landWalk: z.number().nullable(),
  buildingType: z.object({
    mansion: z.boolean(),
    apartment: z.boolean(),
    building: z.boolean(),
    hotel: z.boolean(),
    dormitory: z.boolean(),
    industrialFacility: z.boolean(),
    other: z.boolean(),
  }),
  propertyName: z
    .string()
    .max(
      PROPERTY_NAME_MAX_LENGTH,
      `${PROPERTY_NAME_MAX_LENGTH}文字以内で入力してください`
    ),
  buildingVariant: z.array(z.number()),
  buildDateRange: makeNullableDateRangeSchema(
    BUILD_DATE_MIN,
    () => getSearchMaxDate(),
    "築年は1900年以降、かつ当年以前を入力してください",
    "築年の開始（以降）は、終了（以前）と同じか前の年を入力してください"
  ),
  totalFloorArea: makeAreaRangeSchema(
    "「以上」は「以下」より小さくしてください"
  ),
  exclusiveArea: makeAreaRangeSchema(
    "「以上」は「以下」より小さくしてください"
  ),
  groundFloors: makeIntegerRangeSchema(
    GROUND_FLOORS_MIN,
    GROUND_FLOORS_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  undergroundFloors: makeIntegerRangeSchema(
    UNDERGROUND_FLOORS_MIN,
    UNDERGROUND_FLOORS_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  parkingSpaces: makeIntegerRangeSchema(
    PARKING_SPACES_MIN,
    PARKING_SPACES_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  buildingPrice: makeBigIntRangeSchema(
    "「以上」は「以下」より小さくしてください"
  ),
  buildingWalk: z.number().nullable(),
  buildingAge: makeRangeSchemaWithRestrictMinMax(
    BUILDING_AGE_MIN,
    BUILDING_AGE_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  kaokuNumber: makeIntegerRangeSchema(
    KAOKU_NUMBER_MIN,
    KAOKU_NUMBER_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  landArea: makeAreaRangeSchema("「以上」は「以下」より小さくしてください"),
  buildingArea: makeAreaRangeSchema("「以上」は「以下」より小さくしてください"),
  ownershipType: z.union([z.literal("bunjou"), z.literal("ittou"), z.null()]),
  hotelName: z
    .string()
    .max(
      HOTEL_NAME_MAX_LENGTH,
      `${HOTEL_NAME_MAX_LENGTH}文字以内で入力してください`
    ),
  hotelCategory: z.array(z.number()),
  hotelEstimateAcreage: makeAreaRangeSchema(
    "「以上」は「以下」より小さくしてください"
  ),
  hotelRoomCount: makeIntegerRangeSchema(
    HOTEL_ROOM_COUNT_MIN,
    HOTEL_ROOM_COUNT_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  dormitoryName: z
    .string()
    .max(
      DORMITORY_NAME_MAX_LENGTH,
      `${DORMITORY_NAME_MAX_LENGTH}文字以内で入力してください`
    ),
  industrialFacilityName: z
    .string()
    .max(
      INDUSTRIAL_FACILITY_NAME_MAX_LENGTH,
      `${INDUSTRIAL_FACILITY_NAME_MAX_LENGTH}文字以内で入力してください`
    ),
  dormitoryKind: z.array(z.enum(DORMITORY_KIND_VALUES)),
});

// 定義したZodのスキーマをTypescriptの型に変換
export type FormData = z.infer<typeof FormDataSchema>;

export const MapSearchDialog: React.FC<{
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchResult: (searchResult: RealEstateSearchResult[]) => void;
  setViewLatLng: (point: LatLng) => void;
  hasMapViewingPermission: boolean | null;
}> = (props) => {
  const {
    open,
    setOpen,
    setSearchResult,
    setViewLatLng,
    hasMapViewingPermission,
  } = props;
  const apiClient = useApiClient().apiClient;
  // 最後に結果を出した検索の条件と、その時に地図を寄せた地点。
  // 同じ条件では検索し直さない（API を呼ばず、結果に書き足した住居表示・タグなども残す）
  const lastSearchRef = React.useRef<{
    conditions: string;
    viewLatLng: LatLng;
  } | null>(null);

  const reactHookForm = useForm<FormData>({
    mode: "all",
    defaultValues: {
      prefecture: [],
      city: [],
      address: "",
      areaUsePurpose: [],
      yousekiRatio: { min: null, max: null },
      kenpeiRatio: { min: null, max: null },
      realEstateType: "",
      ownerName: "",
      ownerAddress: "",
      landType: {
        farm: false,
        solar: false,
        parkingLot: false,
      },
      parkingLotName: "",
      totalParkingLotArea: { min: null, max: null },
      estimateAcreage: { min: null, max: null },
      landPrice: { min: null, max: null },
      landWalk: null,
      buildingType: {
        apartment: false,
        mansion: false,
        building: false,
        hotel: false,
        dormitory: false,
        industrialFacility: false,
        other: false,
      },
      propertyName: "",
      buildingVariant: [],
      buildDateRange: [null, null],
      totalFloorArea: { min: null, max: null },
      exclusiveArea: { min: null, max: null },
      groundFloors: { min: null, max: null },
      undergroundFloors: { min: null, max: null },
      parkingSpaces: { min: null, max: null },
      buildingPrice: { min: null, max: null },
      buildingWalk: null,
      buildingAge: { min: null, max: null },
      kaokuNumber: { min: null, max: null },
      landArea: { min: null, max: null },
      buildingArea: { min: null, max: null },
      ownershipType: null,
      hotelName: "",
      hotelCategory: [],
      hotelEstimateAcreage: { min: null, max: null },
      hotelRoomCount: { min: null, max: null },
      dormitoryName: "",
      dormitoryKind: [],
      industrialFacilityName: "",
    },
    resolver: zodResolver(FormDataSchema),
  });

  const handleDatePickerErrorChange =
    (fieldName: "buildDateRange", label: string, index: 0 | 1) =>
    (error: unknown): void => {
      if (error === null || error === "minDate" || error === "maxDate") {
        void reactHookForm.trigger(fieldName);
        return;
      }
      const errorPathMap = {
        buildDateRange: ["buildDateRange.0", "buildDateRange.1"],
      } as const;
      const errorPath = errorPathMap[fieldName][index];
      reactHookForm.setError(errorPath, {
        type: "manual",
        message: `${label}の形式が正しくありません`,
      });
    };

  const isMobile = useIsMobile();

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  /**
   * 「土地の条件から探す」「建物の条件から探す」を押したら、不動産種別をその種別にする
   * （開いている条件と種別がずれないよう、開閉は種別で決める）
   */
  const selectRealEstateType = (value: string) => () => {
    reactHookForm.setValue("realEstateType", value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const { hotelSearch, dormitorySearch, industrialFacilitySearch } =
    useFeatureFlags();
  const hotelSearchEnabled = Boolean(hotelSearch);
  const dormitorySearchEnabled = Boolean(dormitorySearch);
  const industrialFacilitySearchEnabled = Boolean(industrialFacilitySearch);
  const facilitySearchEnabled =
    hotelSearchEnabled ||
    dormitorySearchEnabled ||
    industrialFacilitySearchEnabled;

  // 都道府県一覧
  const { prefectures } = usePrefectures();

  // 用途地域一覧を取得
  const { areaUsePurposeOptions: _areaUsePurposeOptions } =
    useAreaUsePurposeOptions();
  // ホテル形態一覧を取得
  const { hotelCategoryOptions } = useHotelCategoryOptions();

  // 築年月の選択可能最大値
  const maxDate = getSearchMaxDate();

  // 都道府県の変更を検知する
  const prefWatch = reactHookForm.watch("prefecture");

  // 選択している都道府県に応じた市区町村リストを取得する
  const { cityList } = useCityWithExternalState(
    prefWatch.map((value) => value.toString()).filter((value) => value !== "")
  );

  // 建物種別の選択状態を監視
  const watchedBuildingType = reactHookForm.watch("buildingType");
  const isHotelTabActive = hotelSearchEnabled && watchedBuildingType.hotel;
  const isDormitoryTabActive =
    dormitorySearchEnabled && watchedBuildingType.dormitory;
  const isIndustrialFacilityTabActive =
    industrialFacilitySearchEnabled && watchedBuildingType.industrialFacility;
  const activeBuildingSearchTab:
    | "building"
    | "hotel"
    | "dormitory"
    | "industrialFacility" = isHotelTabActive
    ? "hotel"
    : isDormitoryTabActive
    ? "dormitory"
    : isIndustrialFacilityTabActive
    ? "industrialFacility"
    : "building";
  const isBuildingTypeSelected = Object.values(watchedBuildingType).some(
    (v) => v
  );
  // 所有形態の選択状態を監視
  const watchedOwnershipType = reactHookForm.watch("ownershipType");
  // 家屋番号数を表示する条件: 建物種別が未選択、または所有形態が指定なしの場合
  const isKaokuNumberVisible =
    !isBuildingTypeSelected || watchedOwnershipType === null;

  // 駐車場名と駐車場推計地積を監視
  const watchedParkingLotName = reactHookForm.watch("parkingLotName");
  const watchedTotalParkingLotArea = reactHookForm.watch("totalParkingLotArea");

  // 駐車場フィールドに入力があるかを判定
  const hasParkingLotName =
    watchedParkingLotName && watchedParkingLotName.trim() !== "";
  const hasTotalParkingLotArea =
    (watchedTotalParkingLotArea?.min !== null &&
      watchedTotalParkingLotArea?.min !== undefined &&
      watchedTotalParkingLotArea?.min !== 0) ||
    (watchedTotalParkingLotArea?.max !== null &&
      watchedTotalParkingLotArea?.max !== undefined &&
      watchedTotalParkingLotArea?.max !== 0);
  const isParkingLotInputFilled = hasParkingLotName || hasTotalParkingLotArea;

  // 駐車場名または駐車場推計地積に値が入力されたら、自動的に駐車場チェックボックスをON/OFFにする
  React.useEffect(() => {
    const currentLandType = reactHookForm.getValues("landType");
    if (isParkingLotInputFilled) {
      if (!currentLandType.parkingLot) {
        reactHookForm.setValue("landType", {
          ...currentLandType,
          parkingLot: true,
        });
      }
    } else {
      if (currentLandType.parkingLot) {
        reactHookForm.setValue("landType", {
          ...currentLandType,
          parkingLot: false,
        });
      }
    }
  }, [isParkingLotInputFilled, reactHookForm.setValue]);

  const handleTabChange = (
    _e: React.SyntheticEvent,
    newValue: "building" | "hotel" | "dormitory" | "industrialFacility"
  ): void => {
    if (newValue === "hotel") {
      reactHookForm.setValue("buildingType", {
        mansion: false,
        apartment: false,
        building: false,
        hotel: true,
        dormitory: false,
        industrialFacility: false,
        other: false,
      });
      reactHookForm.setValue("ownershipType", null);
      reactHookForm.setValue("kaokuNumber", { min: null, max: null });
      reactHookForm.setValue("propertyName", "");
      reactHookForm.setValue("buildingVariant", []);
      reactHookForm.setValue("exclusiveArea", { min: null, max: null });
      reactHookForm.setValue("buildingAge", { min: null, max: null });
      reactHookForm.setValue("buildDateRange", [null, null]);
      reactHookForm.setValue("totalFloorArea", { min: null, max: null });
      reactHookForm.setValue("groundFloors", { min: null, max: null });
      reactHookForm.setValue("undergroundFloors", { min: null, max: null });
      reactHookForm.setValue("parkingSpaces", { min: null, max: null });
      reactHookForm.setValue("landArea", { min: null, max: null });
      reactHookForm.setValue("buildingArea", { min: null, max: null });
      reactHookForm.setValue("buildingPrice", { min: null, max: null });
      reactHookForm.setValue("buildingWalk", null);
      reactHookForm.setValue("dormitoryName", "");
      reactHookForm.setValue("dormitoryKind", []);
      reactHookForm.setValue("industrialFacilityName", "");
    } else if (newValue === "dormitory") {
      reactHookForm.setValue("buildingType", {
        mansion: false,
        apartment: false,
        building: false,
        hotel: false,
        dormitory: true,
        industrialFacility: false,
        other: false,
      });
      reactHookForm.setValue("ownershipType", null);
      reactHookForm.setValue("kaokuNumber", { min: null, max: null });
      reactHookForm.setValue("propertyName", "");
      reactHookForm.setValue("buildingVariant", []);
      reactHookForm.setValue("exclusiveArea", { min: null, max: null });
      reactHookForm.setValue("buildingAge", { min: null, max: null });
      reactHookForm.setValue("buildDateRange", [null, null]);
      reactHookForm.setValue("totalFloorArea", { min: null, max: null });
      reactHookForm.setValue("groundFloors", { min: null, max: null });
      reactHookForm.setValue("undergroundFloors", { min: null, max: null });
      reactHookForm.setValue("parkingSpaces", { min: null, max: null });
      reactHookForm.setValue("landArea", { min: null, max: null });
      reactHookForm.setValue("buildingArea", { min: null, max: null });
      reactHookForm.setValue("buildingPrice", { min: null, max: null });
      reactHookForm.setValue("buildingWalk", null);
      reactHookForm.setValue("hotelName", "");
      reactHookForm.setValue("hotelCategory", []);
      reactHookForm.setValue("hotelEstimateAcreage", { min: null, max: null });
      reactHookForm.setValue("hotelRoomCount", { min: null, max: null });
      reactHookForm.setValue("industrialFacilityName", "");
    } else if (newValue === "industrialFacility") {
      reactHookForm.setValue("buildingType", {
        mansion: false,
        apartment: false,
        building: false,
        hotel: false,
        dormitory: false,
        industrialFacility: true,
        other: false,
      });
      reactHookForm.setValue("ownershipType", null);
      reactHookForm.setValue("kaokuNumber", { min: null, max: null });
      reactHookForm.setValue("propertyName", "");
      reactHookForm.setValue("buildingVariant", []);
      reactHookForm.setValue("exclusiveArea", { min: null, max: null });
      reactHookForm.setValue("buildingAge", { min: null, max: null });
      reactHookForm.setValue("buildDateRange", [null, null]);
      reactHookForm.setValue("totalFloorArea", { min: null, max: null });
      reactHookForm.setValue("groundFloors", { min: null, max: null });
      reactHookForm.setValue("undergroundFloors", { min: null, max: null });
      reactHookForm.setValue("parkingSpaces", { min: null, max: null });
      reactHookForm.setValue("landArea", { min: null, max: null });
      reactHookForm.setValue("buildingArea", { min: null, max: null });
      reactHookForm.setValue("buildingPrice", { min: null, max: null });
      reactHookForm.setValue("buildingWalk", null);
      reactHookForm.setValue("hotelName", "");
      reactHookForm.setValue("hotelCategory", []);
      reactHookForm.setValue("hotelEstimateAcreage", { min: null, max: null });
      reactHookForm.setValue("hotelRoomCount", { min: null, max: null });
      reactHookForm.setValue("dormitoryName", "");
      reactHookForm.setValue("dormitoryKind", []);
    } else {
      reactHookForm.setValue("buildingType", {
        mansion: false,
        apartment: false,
        building: false,
        hotel: false,
        dormitory: false,
        industrialFacility: false,
        other: false,
      });
      reactHookForm.setValue("hotelName", "");
      reactHookForm.setValue("hotelCategory", []);
      reactHookForm.setValue("hotelEstimateAcreage", { min: null, max: null });
      reactHookForm.setValue("hotelRoomCount", { min: null, max: null });
      reactHookForm.setValue("dormitoryName", "");
      reactHookForm.setValue("dormitoryKind", []);
      reactHookForm.setValue("industrialFacilityName", "");
      reactHookForm.setValue("buildingPrice", { min: null, max: null });
      reactHookForm.setValue("buildingWalk", null);
    }
  };

  // const theme = useTheme();
  // PC では基本の条件を左、土地・建物の詳しい条件を右に並べる（SP は縦に積む）
  const DrawerContent = (
    <Box
      role="presentation"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          // 1fr だと中身の最小幅まで広がって SP ではみ出すので、最小を 0 にする
          xs: "minmax(0, 1fr)",
          [MOBILE_BREAKPOINT]: "minmax(280px, 2fr) minmax(0, 3fr)",
        },
        columnGap: 4,
        alignItems: "start",
      }}
    >
      <Box>
        <Box>
          <Box sx={{ my: 1 }}>
            <Controller
              control={reactHookForm.control}
              name="prefecture"
              render={({ field: { value, onChange }, fieldState }) => {
                return (
                  <>
                    <Label required={true}>都道府県</Label>
                    <CSelect
                      error={Boolean(fieldState.error)}
                      formHelperText={fieldState.error?.message}
                      label="都道府県"
                      value={value.length ? value[0].toString() : ""}
                      onChange={(value) => {
                        onChange(value ? [Number(value)] : []);
                        /* eslint-disable */
                        // @ts-ignore
                        // 市区町村の選択をリセットする
                        reactHookForm.setValue("city", []);
                      }}
                      options={prefectures.map((pref) => ({
                        value: pref.prefCode,
                        label: pref.name,
                      }))}
                    />
                  </>
                );
              }}
            />
          </Box>
          <Box my={1}>
            <Controller
              control={reactHookForm.control}
              name="city"
              render={({ field: { value, onChange }, fieldState }) => {
                return (
                  <>
                    <Label required={true}>市区町村</Label>
                    <CSelect
                      error={Boolean(fieldState.error)}
                      formHelperText={fieldState.error?.message}
                      label="市区町村"
                      value={value.length ? value[0].toString() : ""}
                      onChange={(value) => {
                        onChange(value ? [Number(value)] : []);
                      }}
                      options={
                        cityList?.list.map((city) => ({
                          value: city.id.toString(),
                          label: city.name,
                        })) ?? []
                      }
                    />
                  </>
                );
              }}
            />
          </Box>
          <Box my={1}>
            <Controller
              control={reactHookForm.control}
              name="address"
              render={({ field: { value, onChange } }) => {
                return (
                  <>
                    <Label>大字・町丁目</Label>
                    <TextField
                      label="大字・町丁目"
                      value={value}
                      onChange={onChange}
                      fullWidth={true}
                    />
                  </>
                );
              }}
            />
          </Box>
        </Box>
        <Box>
          <Box sx={{ my: 1 }}>
            <Label required={true}>不動産種別</Label>
            <Box>
              <Controller
                control={reactHookForm.control}
                name={"realEstateType"}
                render={({ field: { value, onChange } }) => {
                  return (
                    <MuiRadioGroup
                      name="realEstateType"
                      value={value}
                      onChange={(e) => {
                        onChange(e.target.value);
                      }}
                    >
                      <MuiFormControlLabel
                        value={REAL_ESTATE_TYPE_MAP.land.value}
                        control={<MuiRadio />}
                        label={REAL_ESTATE_TYPE_MAP.land.label}
                      />
                      <MuiFormControlLabel
                        value={REAL_ESTATE_TYPE_MAP.building.value}
                        control={<MuiRadio />}
                        label={REAL_ESTATE_TYPE_MAP.building.label}
                      />
                    </MuiRadioGroup>
                  );
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ my: 2 }}>
          <Label>所有者情報</Label>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Label
                sx={{ fontWeight: "normal", fontSize: "0.875rem", mb: 0.5 }}
              >
                所有者名 (部分一致)
              </Label>
              <TextField
                {...reactHookForm.register("ownerName")}
                placeholder="所有者名"
                error={Boolean(reactHookForm.formState.errors.ownerName)}
                helperText={reactHookForm.formState.errors.ownerName?.message}
                sx={{ width: "100%" }}
                size="small"
              />
            </Box>
            <Box>
              <Label
                sx={{ fontWeight: "normal", fontSize: "0.875rem", mb: 0.5 }}
              >
                所有者住所 (部分一致)
              </Label>
              <TextField
                {...reactHookForm.register("ownerAddress")}
                placeholder="所有者住所"
                error={Boolean(reactHookForm.formState.errors.ownerAddress)}
                helperText={
                  reactHookForm.formState.errors.ownerAddress?.message
                }
                sx={{ width: "100%" }}
                size="small"
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box>
        <Accordion
          expanded={
            REAL_ESTATE_TYPE_MAP.land.value ===
            reactHookForm.watch("realEstateType")
          }
          onChange={selectRealEstateType(REAL_ESTATE_TYPE_MAP.land.value)}
          square
          sx={{ mb: 1, boxShadow: "none", position: "static" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ flexDirection: "row-reverse" }}
          >
            <Typography sx={{ color: "text.primary" }}>
              土地の条件から探す
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Box sx={{ my: 1 }}>
              <CheckboxGroup label="土地種別">
                <Controller
                  control={reactHookForm.control}
                  name={"landType"}
                  render={({ field: { value, onChange } }) => {
                    const landTypeOptions = [
                      { key: "farm", label: "農地" },
                      { key: "solar", label: "太陽光" },
                      { key: "parkingLot", label: "駐車場" },
                    ] as const;
                    return (
                      <>
                        {landTypeOptions.map((option) => (
                          <Checkbox
                            key={option.key}
                            label={option.label}
                            checked={value[option.key]}
                            onChange={(checked) => {
                              onChange({ ...value, [option.key]: checked });
                            }}
                            disabled={
                              option.key === "parkingLot" &&
                              isParkingLotInputFilled
                            }
                          />
                        ))}
                      </>
                    );
                  }}
                />
              </CheckboxGroup>
            </Box>
            <Box sx={{ my: 1 }}>
              <Label>駐車場名（部分一致）</Label>
              <Controller
                name={"parkingLotName"}
                control={reactHookForm.control}
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <TextField
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      placeholder="駐車場名"
                      sx={{ width: "250px" }}
                      variant="outlined"
                    />
                  );
                }}
              />
            </Box>
            <Box sx={{ my: 1 }}>
              <Label>駐車場 推計地積</Label>
              <Controller
                name={"totalParkingLotArea"}
                control={reactHookForm.control}
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <>
                      <TextField
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        value={
                          value.min === null || value.min === 0 ? "" : value.min
                        }
                        onChange={(event) => {
                          onChange({
                            min:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                            max: value.max,
                          });
                        }}
                        onKeyDown={undefined}
                        onPaste={undefined}
                        sx={{ width: "150px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "10",
                        }}
                      />
                      ㎡以上 〜
                      <TextField
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        value={
                          value.max === null || value.max === 0 ? "" : value.max
                        }
                        onChange={(event) => {
                          onChange({
                            min: value.min,
                            max:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                          });
                        }}
                        onKeyDown={undefined}
                        onPaste={undefined}
                        sx={{ width: "150px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "10",
                        }}
                      />
                      ㎡以下
                    </>
                  );
                }}
              />
            </Box>
            {/*
              用途地域（areaUsePurpose）のUIコンポーネント
              データベースと地図の間で相違があるため、一時的に非表示にしています。
              バックエンド機能は維持されているため、将来的に必要になった場合は以下のコードを復活させてください。
            */}
            {/* <Box sx={{ my: 1 }}>
              <Label>用途地域</Label>
              <Controller
                name={"areaUsePurpose"}
                control={reactHookForm.control}
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <MultiSelect
                      error={Boolean(fieldState.error)}
                      formHelperText={fieldState.error?.message}
                      label="用途地域"
                      value={value.map((value) => value.toString())}
                      onChange={(value) => {
                        onChange(value.map((value) => Number(value)));
                      }}
                      options={areaUsePurposeOptions}
                      maxSelect={5}
                    />
                  );
                }}
              />
            </Box> */}
            <Box sx={{ my: 1 }}>
              <Label>推計地積</Label>
              <Controller
                name={"estimateAcreage"}
                control={reactHookForm.control}
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <>
                      <TextField
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        value={
                          value.min === null || value.min === 0 ? "" : value.min
                        }
                        onChange={(event) => {
                          onChange({
                            min:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                            max: value.max,
                          });
                        }}
                        onKeyDown={undefined}
                        onPaste={undefined}
                        sx={{ width: "150px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "10",
                        }}
                      />
                      ㎡以上 〜
                      <TextField
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        value={
                          value.max === null || value.max === 0 ? "" : value.max
                        }
                        onChange={(event) => {
                          onChange({
                            min: value.min,
                            max:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                          });
                        }}
                        onKeyDown={undefined}
                        onPaste={undefined}
                        sx={{ width: "150px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "10",
                        }}
                      />
                      ㎡以下
                    </>
                  );
                }}
              />
            </Box>
            <Box sx={{ my: 1 }}>
              <Label>公示価格</Label>
              <Controller
                name={"landPrice"}
                control={reactHookForm.control}
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <>
                      <TextField
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        value={
                          value.min === null || value.min === 0 ? "" : value.min
                        }
                        onChange={(event) => {
                          onChange({
                            min:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                            max: value.max,
                          });
                        }}
                        onKeyDown={undefined}
                        onPaste={undefined}
                        sx={{ width: "150px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "10",
                        }}
                      />
                      円/㎡以上 〜
                      <TextField
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        value={
                          value.max === null || value.max === 0 ? "" : value.max
                        }
                        onChange={(event) => {
                          onChange({
                            min: value.min,
                            max:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                          });
                        }}
                        onKeyDown={undefined}
                        onPaste={undefined}
                        sx={{ width: "150px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "10",
                        }}
                      />
                      円/㎡以下
                    </>
                  );
                }}
              />
            </Box>
            {/*
              容積率（yousekiRatio）のUIコンポーネント
              データベースと地図の間で相違があるため、一時的に非表示にしています。
              バックエンド機能は維持されているため、将来的に必要になった場合は以下のコードを復活させてください。
            */}
            {/* <Box sx={{ my: 1 }}>
              <Label>容積率</Label>
              <Controller
                name={"yousekiRatio"}
                control={reactHookForm.control}
                render={({
                  field: { value, onChange },
                  formState,
                  fieldState,
                }) => {
                  return (
                    <>
                      <TextField
                        error={Boolean(
                          formState.errors.yousekiRatio?.min ?? fieldState.error
                        )}
                        helperText={
                          formState.errors.yousekiRatio?.min?.message ??
                          fieldState.error?.message
                        }
                        value={
                          value.min === null || value.min === 0 ? "" : value.min
                        }
                        onChange={(event) => {
                          onChange({
                            min:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                            max: value.max,
                          });
                        }}
                        onKeyDown={undefined}
                        onPaste={undefined}
                        sx={{ width: "150px", top: "50%" }}
                        inputProps={{
                          min: 50,
                          max: 1300,
                          type: "number",
                          step: "10",
                        }}
                      />
                      %以上 〜
                      <TextField
                        error={Boolean(
                          formState.errors.yousekiRatio?.max ?? fieldState.error
                        )}
                        helperText={
                          formState.errors.yousekiRatio?.max?.message ??
                          fieldState.error?.message
                        }
                        value={
                          value.max === null || value.max === 0 ? "" : value.max
                        }
                        onChange={(event) => {
                          onChange({
                            min: value.min,
                            max:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                          });
                        }}
                        onKeyDown={undefined}
                        onPaste={undefined}
                        sx={{ width: "150px", top: "50%" }}
                        inputProps={{
                          min: 50,
                          max: 1300,
                          type: "number",
                          step: "10",
                        }}
                      />
                      %以下
                    </>
                  );
                }}
              />
            </Box> */}
            {/*
              建ぺい率（kenpeiRatio）のUIコンポーネント
              データベースと地図の間で相違があるため、一時的に非表示にしています。
              バックエンド機能は維持されているため、将来的に必要になった場合は以下のコードを復活させてください。
            */}
            {/* <Box sx={{ my: 1 }}>
              <Label>建ぺい率</Label>
              <Controller
                name={"kenpeiRatio"}
                control={reactHookForm.control}
                render={({
                  field: { value, onChange },
                  formState,
                  fieldState,
                }) => {
                  return (
                    <>
                      <TextField
                        error={Boolean(
                          formState.errors.kenpeiRatio?.min ?? fieldState.error
                        )}
                        helperText={
                          formState.errors.kenpeiRatio?.min?.message ??
                          fieldState.error?.message
                        }
                        value={
                          value.min === null || value.min === 0 ? "" : value.min
                        }
                        onChange={(event) => {
                          onChange({
                            min:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                            max: value.max,
                          });
                        }}
                        onKeyDown={undefined}
                        onPaste={undefined}
                        sx={{ width: "150px", top: "50%" }}
                        inputProps={{
                          min: 30,
                          max: 80,
                          type: "number",
                          step: "10",
                        }}
                      />
                      %以上 〜
                      <TextField
                        error={Boolean(
                          formState.errors.kenpeiRatio?.max ?? fieldState.error
                        )}
                        helperText={
                          formState.errors.kenpeiRatio?.max?.message ??
                          fieldState.error?.message
                        }
                        value={
                          value.max === null || value.max === 0 ? "" : value.max
                        }
                        onChange={(event) => {
                          onChange({
                            min: value.min,
                            max:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                          });
                        }}
                        onKeyDown={undefined}
                        onPaste={undefined}
                        sx={{ width: "150px", top: "50%" }}
                        inputProps={{
                          min: 30,
                          max: 80,
                          type: "number",
                          step: "10",
                        }}
                      />
                      %以下
                    </>
                  );
                }}
              />
            </Box> */}
            <Box sx={{ my: 1 }}>
              <Label>最寄駅からの徒歩時間</Label>
              <Controller
                name={"landWalk"}
                control={reactHookForm.control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <Select
                      value={value === null ? -1 : value}
                      onChange={(e) => {
                        onChange(e.target.value);
                      }}
                      sx={{ width: "150px", minHeight: "56px" }}
                      variant="outlined"
                    >
                      <MenuItem key={-1} value={-1}>
                        指定なし
                      </MenuItem>
                      {[...Array(60).keys()]
                        .filter((val) => val >= 1)
                        .map((val) => (
                          <MenuItem key={val} value={val}>
                            {val}分以内
                          </MenuItem>
                        ))}
                      <MenuItem key={60} value={60}>
                        60分以上
                      </MenuItem>
                    </Select>
                  );
                }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={
            REAL_ESTATE_TYPE_MAP.building.value ===
            reactHookForm.watch("realEstateType")
          }
          onChange={selectRealEstateType(REAL_ESTATE_TYPE_MAP.building.value)}
          square
          sx={{ mb: 1, boxShadow: "none", position: "static" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ flexDirection: "row-reverse" }}
          >
            <Typography sx={{ color: "text.primary" }}>
              建物の条件から探す
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            {facilitySearchEnabled && (
              // 探す対象をすべて並べて見せ、押して選ぶ（タブだと横にスクロールしないと見えなかった）
              <Box
                role="radiogroup"
                aria-label="探す建物"
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    [MOBILE_BREAKPOINT]: "1fr 1fr",
                  },
                  gap: 1,
                  mt: 1,
                }}
              >
                {(
                  [
                    {
                      value: "building",
                      icon: <ApartmentIcon fontSize="small" />,
                      label: "建物(マンション・アパート・ビル等)を探す",
                      enabled: true,
                    },
                    {
                      value: "hotel",
                      icon: <LocalHotelIcon fontSize="small" />,
                      label: "ホテルを探す",
                      enabled: hotelSearchEnabled,
                    },
                    {
                      value: "dormitory",
                      icon: <HomeWorkIcon fontSize="small" />,
                      label: "寮・社宅を探す",
                      enabled: dormitorySearchEnabled,
                    },
                    {
                      value: "industrialFacility",
                      icon: <FactoryIcon fontSize="small" />,
                      label: "工場/倉庫を探す",
                      enabled: industrialFacilitySearchEnabled,
                    },
                  ] as const
                )
                  .filter((option) => option.enabled)
                  .map((option) => (
                    <ToggleButton
                      key={option.value}
                      value={option.value}
                      selected={activeBuildingSearchTab === option.value}
                      role="radio"
                      aria-checked={activeBuildingSearchTab === option.value}
                      onChange={(e) => {
                        // ToggleButton は選んでいるものを押し直しても onChange が呼ばれる。
                        // 種別を替えると条件を消すので、押し直しでは何もしない
                        if (activeBuildingSearchTab === option.value) return;
                        handleTabChange(e, option.value);
                      }}
                      color="primary"
                      size="small"
                      sx={{
                        justifyContent: "flex-start",
                        gap: 1,
                        textTransform: "none",
                        textAlign: "left",
                        lineHeight: 1.4,
                        // 選んでいるものは枠も色を付けて見分けやすくする
                        "&.Mui-selected": { borderColor: "primary.main" },
                      }}
                    >
                      {option.icon}
                      {option.label}
                    </ToggleButton>
                  ))}
              </Box>
            )}
            {(!facilitySearchEnabled ||
              (!isHotelTabActive &&
                !isDormitoryTabActive &&
                !isIndustrialFacilityTabActive)) && (
              <>
                <Box sx={{ mt: 2 }}>
                  <CheckboxGroup label="建物種別">
                    <Controller
                      control={reactHookForm.control}
                      name={"buildingType"}
                      render={({ field: { value, onChange } }) => {
                        const buildingTypeOptions = [
                          { key: "mansion", label: "マンション" },
                          { key: "apartment", label: "アパート" },
                          { key: "building", label: "ビル" },
                          { key: "other", label: "その他" },
                        ] as const;
                        return (
                          <>
                            {buildingTypeOptions.map((option) => (
                              <Checkbox
                                key={option.key}
                                label={option.label}
                                checked={value[option.key]}
                                onChange={(checked) => {
                                  const newValue = {
                                    ...value,
                                    [option.key]: checked,
                                  };
                                  onChange(newValue);

                                  // 建物種別の選択状態に応じてownershipTypeを連動
                                  const anySelected = Object.values(
                                    newValue
                                  ).some((v) => v);
                                  if (anySelected) {
                                    // 建物種別が選択されたら家屋番号数をクリア
                                    reactHookForm.setValue("kaokuNumber", {
                                      min: null,
                                      max: null,
                                    });
                                  } else {
                                    // 建物種別が全て解除されたらownershipTypeもクリア
                                    reactHookForm.setValue(
                                      "ownershipType",
                                      null
                                    );
                                  }
                                }}
                              />
                            ))}
                          </>
                        );
                      }}
                    />
                  </CheckboxGroup>
                  {/* 建物種別が1つ以上選択されている場合のみ「所有形態」を表示 */}
                  {isBuildingTypeSelected && (
                    <Box sx={{ mt: 1 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Label sx={{ mb: 0 }}>所有形態</Label>
                        <Tooltip
                          title={
                            <span>
                              一棟はR.E.DATA独自の定義です。詳細は
                              <a
                                href="https://www.notion.so/trustart/10-18414754f2df80e8954acd41b30740cf?source=copy_link#18a14754f2df80fabaa7d74649b495b6"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#90caf9" }}
                              >
                                マニュアル
                              </a>
                              をご確認ください
                            </span>
                          }
                        >
                          <HelpOutlineIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "help",
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <Controller
                        control={reactHookForm.control}
                        name="ownershipType"
                        render={({ field: { value, onChange } }) => (
                          <MuiRadioGroup
                            row
                            value={value ?? ""}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              onChange(newValue === "" ? null : newValue);
                              // 一棟または分譲を選択したら家屋番号数をクリア
                              if (newValue !== "") {
                                reactHookForm.setValue("kaokuNumber", {
                                  min: null,
                                  max: null,
                                });
                              }
                            }}
                          >
                            <MuiFormControlLabel
                              value=""
                              control={<MuiRadio />}
                              label="指定なし"
                            />
                            <MuiFormControlLabel
                              value="ittou"
                              control={<MuiRadio />}
                              label="一棟"
                            />
                            <MuiFormControlLabel
                              value="bunjou"
                              control={<MuiRadio />}
                              label="分譲"
                            />
                          </MuiRadioGroup>
                        )}
                      />
                    </Box>
                  )}
                </Box>
                {isKaokuNumberVisible && (
                  <Box sx={{ my: 1 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Label sx={{ mb: 0 }}>
                        家屋番号数（対象建物下の地番上の家屋番号数）
                      </Label>
                      <Tooltip title="一棟収益（賃貸）物件の検索をされたい場合、1以上4以下とご入力ください">
                        <HelpOutlineIcon
                          sx={{
                            fontSize: 18,
                            color: "text.secondary",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <Controller
                      control={reactHookForm.control}
                      name="kaokuNumber"
                      render={({ field: { value, onChange }, fieldState }) => (
                        <>
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            value={
                              value.min === null || value.min === 0
                                ? ""
                                : value.min
                            }
                            label="以上"
                            onChange={(e) => {
                              onChange({
                                min:
                                  e.target.value === ""
                                    ? null
                                    : Number(e.target.value),
                                max: value.max,
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", mr: 1 }}
                            inputProps={{ min: 0, type: "number", step: "1" }}
                          />
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            value={
                              value.max === null || value.max === 0
                                ? ""
                                : value.max
                            }
                            label="以下"
                            onChange={(e) => {
                              onChange({
                                min: value.min,
                                max:
                                  e.target.value === ""
                                    ? null
                                    : Number(e.target.value),
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px" }}
                            inputProps={{ min: 0, type: "number", step: "1" }}
                          />
                        </>
                      )}
                    />
                  </Box>
                )}
                <Box sx={{ my: 1 }}>
                  <Label>建物名</Label>
                  <Controller
                    control={reactHookForm.control}
                    name="propertyName"
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        size="small"
                        placeholder="建物名"
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        sx={{ width: "200px" }}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>構造</Label>
                  <Controller
                    control={reactHookForm.control}
                    name="buildingVariant"
                    render={({ field: { value, onChange }, fieldState }) => {
                      return (
                        <MultiSelect
                          error={Boolean(fieldState.error)}
                          formHelperText={fieldState.error?.message}
                          label="構造"
                          value={value.map((value) => value.toString())}
                          onChange={(value) => {
                            onChange(value.map((value) => Number(value)));
                          }}
                          options={BUILDING_VARIANT_OPTIONS}
                        />
                      );
                    }}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>専有面積</Label>
                  <Controller
                    control={reactHookForm.control}
                    name={"exclusiveArea"}
                    render={({ field: { value, onChange }, fieldState }) => {
                      return (
                        <>
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.min === null || value.min === 0
                                ? ""
                                : value.min
                            }
                            onChange={(event) => {
                              onChange({
                                min:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                                max: value.max,
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          ㎡以上 〜
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.max === null || value.max === 0
                                ? ""
                                : value.max
                            }
                            onChange={(event) => {
                              onChange({
                                min: value.min,
                                max:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          ㎡以下
                        </>
                      );
                    }}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>築年数</Label>
                  <Controller
                    control={reactHookForm.control}
                    name={"buildingAge"}
                    render={({ field: { value, onChange }, fieldState }) => (
                      <>
                        <TextField
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          value={
                            value.min === null || value.min === 0
                              ? ""
                              : value.min
                          }
                          label="年以上"
                          onChange={(event) => {
                            onChange({
                              min:
                                event.target.value === ""
                                  ? null
                                  : Number(event.target.value),
                              max: value.max,
                            });
                          }}
                          onKeyDown={preventNonNumericValueWhenKeyDown}
                          onPaste={preventNonNumericValueWhenPaste}
                          sx={{ width: "120px", top: "50%", mr: 1 }}
                          inputProps={{
                            min: 0,
                            type: "number",
                            step: "10",
                          }}
                        />
                        <TextField
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          value={
                            value.max === null || value.max === 0
                              ? ""
                              : value.max
                          }
                          id="buildingAgeMax"
                          label="年以下"
                          onChange={(event) => {
                            onChange({
                              min: value.min,
                              max:
                                event.target.value === ""
                                  ? null
                                  : Number(event.target.value),
                            });
                          }}
                          onKeyDown={preventNonNumericValueWhenKeyDown}
                          onPaste={preventNonNumericValueWhenPaste}
                          sx={{ width: "120px", top: "50%" }}
                          inputProps={{
                            min: 0,
                            type: "number",
                            step: "10",
                          }}
                        />
                      </>
                    )}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>築年</Label>
                  <Stack spacing={2}>
                    <Controller
                      control={reactHookForm.control}
                      name={"buildDateRange"}
                      render={({ field: { value, onChange }, fieldState }) => {
                        const rangeErrors = fieldState.error as
                          | Array<{ message?: string } | undefined>
                          | undefined;
                        return (
                          <DateRangePicker
                            startDateLabel="以降"
                            startDate={value[0]}
                            onChangeStartDate={(date) => {
                              onChange([date, value[1]]);
                            }}
                            onStartDateErrorChange={handleDatePickerErrorChange(
                              "buildDateRange",
                              "築年",
                              0
                            )}
                            endDateLabel="以前"
                            endDate={value[1]}
                            minDate={BUILD_DATE_MIN}
                            maxDate={maxDate}
                            onChangeEndDate={(date) => {
                              onChange([value[0], date]);
                            }}
                            onEndDateErrorChange={handleDatePickerErrorChange(
                              "buildDateRange",
                              "築年",
                              1
                            )}
                            inputFormat="yyyy"
                            mask="____"
                            views={["year"]}
                            errorStart={rangeErrors?.[0]?.message}
                            errorEnd={rangeErrors?.[1]?.message}
                          />
                        );
                      }}
                    />
                  </Stack>
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>延べ床面積</Label>
                  <Controller
                    control={reactHookForm.control}
                    name={"totalFloorArea"}
                    render={({ field: { value, onChange }, fieldState }) => {
                      return (
                        <>
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.min === null || value.min === 0
                                ? ""
                                : value.min
                            }
                            onChange={(event) => {
                              onChange({
                                min:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                                max: value.max,
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          ㎡以上 〜
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.max === null || value.max === 0
                                ? ""
                                : value.max
                            }
                            onChange={(event) => {
                              onChange({
                                min: value.min,
                                max:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          ㎡以下
                        </>
                      );
                    }}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>地上階</Label>
                  <Controller
                    control={reactHookForm.control}
                    name={"groundFloors"}
                    render={({ field: { value, onChange }, fieldState }) => {
                      return (
                        <>
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.min === null || value.min === 0
                                ? ""
                                : value.min
                            }
                            onChange={(event) => {
                              onChange({
                                min:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                                max: value.max,
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          階以上 〜
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.max === null || value.max === 0
                                ? ""
                                : value.max
                            }
                            onChange={(event) => {
                              onChange({
                                min: value.min,
                                max:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          階以下
                        </>
                      );
                    }}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>地下階</Label>
                  <Controller
                    control={reactHookForm.control}
                    name={"undergroundFloors"}
                    render={({ field: { value, onChange }, fieldState }) => {
                      return (
                        <>
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.min === null || value.min === 0
                                ? ""
                                : value.min
                            }
                            onChange={(event) => {
                              onChange({
                                min:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                                max: value.max,
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          階以上 〜
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.max === null || value.max === 0
                                ? ""
                                : value.max
                            }
                            onChange={(event) => {
                              onChange({
                                min: value.min,
                                max:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          階以下
                        </>
                      );
                    }}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>駐車場数</Label>
                  <Controller
                    control={reactHookForm.control}
                    name={"parkingSpaces"}
                    render={({ field: { value, onChange }, fieldState }) => {
                      return (
                        <>
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.min === null || value.min === 0
                                ? ""
                                : value.min
                            }
                            onChange={(event) => {
                              onChange({
                                min:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                                max: value.max,
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          以上 〜
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.max === null || value.max === 0
                                ? ""
                                : value.max
                            }
                            onChange={(event) => {
                              onChange({
                                min: value.min,
                                max:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          以下
                        </>
                      );
                    }}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>土地面積</Label>
                  <Controller
                    control={reactHookForm.control}
                    name={"landArea"}
                    render={({ field: { value, onChange }, fieldState }) => {
                      return (
                        <>
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.min === null || value.min === 0
                                ? ""
                                : value.min
                            }
                            onChange={(event) => {
                              onChange({
                                min:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                                max: value.max,
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          ㎡以上 〜
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.max === null || value.max === 0
                                ? ""
                                : value.max
                            }
                            onChange={(event) => {
                              onChange({
                                min: value.min,
                                max:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          ㎡以下
                        </>
                      );
                    }}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>建物面積</Label>
                  <Controller
                    control={reactHookForm.control}
                    name={"buildingArea"}
                    render={({ field: { value, onChange }, fieldState }) => {
                      return (
                        <>
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.min === null || value.min === 0
                                ? ""
                                : value.min
                            }
                            onChange={(event) => {
                              onChange({
                                min:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                                max: value.max,
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          ㎡以上 〜
                          <TextField
                            size="small"
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            value={
                              value.max === null || value.max === 0
                                ? ""
                                : value.max
                            }
                            onChange={(event) => {
                              onChange({
                                min: value.min,
                                max:
                                  event.target.value === ""
                                    ? null
                                    : Number(event.target.value),
                              });
                            }}
                            onKeyDown={preventNonNumericValueWhenKeyDown}
                            onPaste={preventNonNumericValueWhenPaste}
                            sx={{ width: "90px", top: "50%" }}
                            inputProps={{
                              min: 0,
                              type: "number",
                              step: "1",
                            }}
                          />
                          ㎡以下
                        </>
                      );
                    }}
                  />
                </Box>
              </>
            )}
            {hotelSearchEnabled && watchedBuildingType.hotel && (
              <>
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Label>ホテル名</Label>
                  <Controller
                    control={reactHookForm.control}
                    name="hotelName"
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        size="small"
                        placeholder="ホテル名"
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        sx={{ width: "200px" }}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>ホテル形態</Label>
                  <Controller
                    control={reactHookForm.control}
                    name="hotelCategory"
                    render={({ field: { value, onChange }, fieldState }) => (
                      <MultiSelect
                        error={Boolean(fieldState.error)}
                        formHelperText={fieldState.error?.message}
                        label="ホテル形態"
                        value={value.map((v) => v.toString())}
                        onChange={(v) => onChange(v.map((v) => Number(v)))}
                        options={hotelCategoryOptions}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>ホテル 推計地積</Label>
                  <Controller
                    control={reactHookForm.control}
                    name={"hotelEstimateAcreage"}
                    render={({ field: { value, onChange }, fieldState }) => (
                      <>
                        <TextField
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          helperText={fieldState.error?.message}
                          value={
                            value.min === null || value.min === 0
                              ? ""
                              : value.min
                          }
                          onChange={(event) => {
                            onChange({
                              min:
                                event.target.value === ""
                                  ? null
                                  : Number(event.target.value),
                              max: value.max,
                            });
                          }}
                          onKeyDown={preventNonNumericValueWhenKeyDown}
                          onPaste={preventNonNumericValueWhenPaste}
                          sx={{ width: "90px", top: "50%" }}
                          inputProps={{ min: 0, type: "number", step: "10" }}
                        />
                        ㎡以上 〜
                        <TextField
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          helperText={fieldState.error?.message}
                          value={
                            value.max === null || value.max === 0
                              ? ""
                              : value.max
                          }
                          onChange={(event) => {
                            onChange({
                              min: value.min,
                              max:
                                event.target.value === ""
                                  ? null
                                  : Number(event.target.value),
                            });
                          }}
                          onKeyDown={preventNonNumericValueWhenKeyDown}
                          onPaste={preventNonNumericValueWhenPaste}
                          sx={{ width: "90px", top: "50%" }}
                          inputProps={{ min: 0, type: "number", step: "10" }}
                        />
                        ㎡以下
                      </>
                    )}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>ホテル 部屋数</Label>
                  <Controller
                    control={reactHookForm.control}
                    name={"hotelRoomCount"}
                    render={({ field: { value, onChange }, fieldState }) => (
                      <>
                        <TextField
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          helperText={fieldState.error?.message}
                          value={
                            value.min === null || value.min === 0
                              ? ""
                              : value.min
                          }
                          onChange={(event) => {
                            onChange({
                              min:
                                event.target.value === ""
                                  ? null
                                  : Number(event.target.value),
                              max: value.max,
                            });
                          }}
                          onKeyDown={preventNonNumericValueWhenKeyDown}
                          onPaste={preventNonNumericValueWhenPaste}
                          sx={{ width: "90px", top: "50%" }}
                          inputProps={{ min: 0, type: "number", step: "1" }}
                        />
                        以上 〜
                        <TextField
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          helperText={fieldState.error?.message}
                          value={
                            value.max === null || value.max === 0
                              ? ""
                              : value.max
                          }
                          onChange={(event) => {
                            onChange({
                              min: value.min,
                              max:
                                event.target.value === ""
                                  ? null
                                  : Number(event.target.value),
                            });
                          }}
                          onKeyDown={preventNonNumericValueWhenKeyDown}
                          onPaste={preventNonNumericValueWhenPaste}
                          sx={{ width: "90px", top: "50%" }}
                          inputProps={{ min: 0, type: "number", step: "1" }}
                        />
                        以下
                      </>
                    )}
                  />
                </Box>
              </>
            )}
            {dormitorySearchEnabled && watchedBuildingType.dormitory && (
              <>
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Label>寮・社宅名</Label>
                  <Controller
                    control={reactHookForm.control}
                    name="dormitoryName"
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        size="small"
                        placeholder="寮・社宅名"
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        sx={{ width: "200px" }}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <Label>種別</Label>
                  <Controller
                    control={reactHookForm.control}
                    name="dormitoryKind"
                    render={({ field: { value, onChange }, fieldState }) => (
                      <MultiSelect
                        error={Boolean(fieldState.error)}
                        formHelperText={fieldState.error?.message}
                        label="種別"
                        value={value}
                        onChange={onChange}
                        options={DORMITORY_KIND_OPTIONS}
                      />
                    )}
                  />
                </Box>
              </>
            )}
            {industrialFacilitySearchEnabled &&
              watchedBuildingType.industrialFacility && (
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Label>工場/倉庫名</Label>
                  <Controller
                    control={reactHookForm.control}
                    name="industrialFacilityName"
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        size="small"
                        placeholder="工場/倉庫名"
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        sx={{ width: "200px" }}
                      />
                    )}
                  />
                </Box>
              )}
            <Box sx={{ my: 1 }}>
              <Label>公示価格</Label>
              <Controller
                control={reactHookForm.control}
                name={"buildingPrice"}
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <>
                      <TextField
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        value={
                          value.min === null || value.min === 0 ? "" : value.min
                        }
                        onChange={(event) => {
                          onChange({
                            min:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                            max: value.max,
                          });
                        }}
                        onKeyDown={preventNonNumericValueWhenKeyDown}
                        onPaste={preventNonNumericValueWhenPaste}
                        sx={{ width: "90px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "10",
                        }}
                      />
                      円/㎡以上 〜
                      <TextField
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        value={
                          value.max === null || value.max === 0 ? "" : value.max
                        }
                        onChange={(event) => {
                          onChange({
                            min: value.min,
                            max:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                          });
                        }}
                        onKeyDown={preventNonNumericValueWhenKeyDown}
                        onPaste={preventNonNumericValueWhenPaste}
                        sx={{ width: "90px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "10",
                        }}
                      />
                      円/㎡以下
                    </>
                  );
                }}
              />
            </Box>
            <Box sx={{ my: 1 }}>
              <Label>最寄駅からの徒歩時間</Label>
              <Controller
                control={reactHookForm.control}
                name="buildingWalk"
                render={({ field: { value, onChange } }) => {
                  return (
                    <Select
                      size="small"
                      value={value === null ? -1 : value}
                      onChange={(e) => {
                        onChange(e.target.value === -1 ? null : e.target.value);
                      }}
                      sx={{ width: "150px" }}
                      variant="outlined"
                    >
                      <MenuItem key={-1} value={-1}>
                        指定なし
                      </MenuItem>
                      {[...Array(60).keys()]
                        .filter((val) => val >= 1)
                        .map((val) => (
                          <MenuItem key={val} value={val}>
                            {val}分以内
                          </MenuItem>
                        ))}
                      <MenuItem key={60} value={60}>
                        60分以上
                      </MenuItem>
                    </Select>
                  );
                }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );

  // 入力した条件をすべて初めの状態に戻す（検索結果は消さない）
  const ClearButton = (
    // SP はボタンを横いっぱいにせず、文字の幅にする
    <Box sx={{ width: { xs: "fit-content", [MOBILE_BREAKPOINT]: 120 } }}>
      <Button
        label={"クリア"}
        onClick={() => {
          reactHookForm.reset();
        }}
        size={SizingWrapperStyle.INHERIT}
        variant={ButtonVariantOption.Outlined}
        disabled={reactHookForm.formState.isSubmitting}
      />
    </Box>
  );

  // 検索ボタンはモーダルの下端に固定し、条件が長くてもスクロールせずに押せるようにする
  const SearchButton = (
    <Box sx={{ width: { xs: "fit-content", [MOBILE_BREAKPOINT]: 240 } }}>
      <Button
        label={"検索"}
        onClick={() => {
          (async () => {
            const submit = reactHookForm.handleSubmit(
              async (data) => {
                if (
                  !hasMapViewingPermission ||
                  !reactHookForm.formState.isValid
                ) {
                  return;
                }
                const conditions = JSON.stringify(data);
                if (lastSearchRef.current?.conditions === conditions) {
                  setViewLatLng(lastSearchRef.current.viewLatLng);
                  setOpen(false);
                  toast.info("同じ条件の検索結果を表示しています");
                  return;
                }
                const result = await getMapRealEstateSearchAPI(apiClient, data);
                if (result?.status === HttpStatusCode.Ok && !result?.timeout) {
                  if (
                    result.data?.count &&
                    result.data.count > MAX_SEARCH_RESULT_COUNT
                  ) {
                    toast.info(
                      `検索結果の件数は${result.data.count.toLocaleString()}件です。${MAX_SEARCH_RESULT_COUNT.toLocaleString()}件以下になるように条件を絞り込んでください。`
                    );
                    return;
                  }
                  const newSearchResult =
                    result.data?.list?.map((item, index) => {
                      return {
                        id: index + 1,
                        landId: item.landId,
                        buildingId: item.buildingId,
                        buildingName: item.buildingName,
                        hotelName: item.hotelName,
                        dormitoryName: item.dormitoryName,
                        industrialFacilityName: item.industrialFacilityName,
                        dormitoryKind: item.dormitoryKind,
                        parkingLotName: item.parkingLotName,
                        realEstateType: item.realEstateType,
                        selected: false,
                        bookType: getBookTypeFromRealEstateTypeLabel(
                          item.realEstateType
                        ),
                        prefName: item.prefName,
                        cityName: item.cityName,
                        location: item.location,
                        chibanKaokuNumber: item.chibanKaokuNumber,
                        address: item.address,
                        area: item.area,
                        latitude: item.latitude,
                        longitude: item.longitude,
                        isFeedOrigin: false,
                        // 土地の住居表示は検索時に取らず、ピンを開いた時に取る
                        ...(needsLandAddress({
                          bookType: getBookTypeFromRealEstateTypeLabel(
                            item.realEstateType
                          ),
                          address: item.address,
                        })
                          ? { landAddressPending: true }
                          : {}),
                        rawData: {
                          address: item.address,
                          lat: item.latitude,
                          lng: item.longitude,
                          prefName: item.prefName,
                          cityName: item.cityName,
                          ooazaName: null,
                          azaName: null,
                          chiban: item.chibanKaokuNumber,
                          addressChiban: "",
                          polygon: undefined,
                          area: item.area,
                        },
                      };
                    }) || [];
                  if (newSearchResult.length) {
                    const viewLatLng = new LatLng(
                      newSearchResult[0].latitude,
                      newSearchResult[0].longitude
                    );
                    setSearchResult(newSearchResult);
                    setViewLatLng(viewLatLng);
                    lastSearchRef.current = { conditions, viewLatLng };
                    setOpen(false);
                    toast.info("検索結果が表示されました");
                  } else {
                    toast.info("検索結果がありません");
                  }
                } else if (
                  result?.status === HttpStatusCode.Ok &&
                  result?.timeout
                ) {
                  toast.info(
                    "該当するデータが多すぎて処理できませんでした。より詳細な条件で再度検索してください。"
                  );
                  return;
                } else if (
                  result?.status &&
                  result.status >= HttpStatusCode.InternalServerError
                ) {
                  toast.error("予期せぬエラーが発生しました");
                  Sentry.captureException(
                    "不動産検索APIの呼び出しに失敗しました"
                  );
                } else {
                  toast.info("再度検索を実行してください");
                }
              },
              (errors, event) => {
                console.log("バリデーショ失敗", errors);
                Sentry.captureException(
                  "不動産検索APIの呼び出しに失敗しました"
                );
              }
            );
            await submit();
          })();
        }}
        size={SizingWrapperStyle.INHERIT}
        variant={ButtonVariantOption.Contained}
        disabled={!hasMapViewingPermission || !reactHookForm.formState.isValid}
        hasLoading
        loading={reactHookForm.formState.isSubmitting}
      />
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // 条件の入力中に外側を押して閉じないよう、閉じるのは右上の × だけにする
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        setOpen(false);
      }}
      // 条件を2段に並べるため広く取る。高さは画面いっぱい（MUI の上限）に固定し、中だけスクロールする。
      // 中身に合わせると、種別や探す建物を切り替えるたびにモーダルの高さが変わって見づらいため。
      // SP は全画面で出す
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { height: { [MOBILE_BREAKPOINT]: "calc(100% - 64px)" } },
      }}
      scroll="paper"
      keepMounted
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6">不動産検索</Typography>
        <Tooltip title={"クリックで閉じる"} placement={"left"}>
          <IconButton onClick={toggleDrawer(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>{DrawerContent}</DialogContent>
      <DialogActions disableSpacing sx={{ px: 3, py: 2, gap: 1 }}>
        {ClearButton}
        {SearchButton}
      </DialogActions>
    </Dialog>
  );
};
